import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { StockArticle, StockMouvement } from '../models/models';
import api from './api';

// Période par défaut = 1 mois (today - 30j → today). Mémorisé pour éviter
// que les rafraîchissements de redux-toolkit recréent des bornes différentes.
const today    = new Date();
const monthAgo = new Date(); monthAgo.setMonth(today.getMonth() - 1);
const toIsoDate = (d: Date) => d.toISOString().split('T')[0];

interface StockFilters {
  code: string | null;
  designation: string | null;
  famille_id: number | null;
  depot_id: number | null;
  // Filtre dept page-local (distinct de l'injection axios `departement_id`).
  // - null   = "Tous les départements" (le controller Stock passe NULL au SP)
  // - number = filtre explicite sur ce dept
  // Initialisé depuis le contexte global au mount de la page.
  filter_dept_id: number | null;
  track_stock: 0 | 1 | null;
  visible: 0 | 1 | null;
  stock_sous_securite: 0 | 1 | null;
  // 1 = masque les articles a stock nul (defaut), 0 = les affiche.
  // Pertinent uniquement pour l'onglet "Etat du stock".
  masquer_stock_zero: 0 | 1 | null;
  date_debut: string | null;          // utilisé uniquement par les mouvements
  date_fin: string | null;            // utilisé uniquement par les mouvements
  limit: number;
  offset: number;
}

interface StockState {
  // Onglet "État du stock"
  stockData: StockArticle[];
  total: number;
  totalPages: number;
  currentPage: number;
  totalValoAchat: number;   // valorisation achat sur tout le stock filtré (hors pagination)
  totalValoVente: number;   // valorisation vente HT sur tout le stock filtré (hors pagination)
  totalValoVenteTtc: number; // valorisation vente TTC sur tout le stock filtré (hors pagination)
  totalQuantite: number;    // Σ stock_reel sur tout le stock filtré, toutes unités confondues
  // Onglet "Mouvements de stock"
  mouvementData: StockMouvement[];
  totalMouvement: number;
  totalPagesMouvement: number;
  currentPageMouvement: number;
  // Totaux valorisés des mouvements (hors pagination, sur tout le filtre)
  totalEntreesQte: number;
  totalSortiesQte: number;
  totalEntreesValeur: number;
  totalSortiesValeur: number;
  // Commun
  loading: boolean;
  error: string | null;
  filters: StockFilters;
}

const initialFilters: StockFilters = {
  code: null,
  designation: null,
  famille_id: null,
  depot_id: null,
  filter_dept_id: null,
  track_stock: 1,
  visible: 1,
  stock_sous_securite: null,
  masquer_stock_zero: 1,
  date_debut: toIsoDate(monthAgo),
  date_fin:   toIsoDate(today),
  limit: 10,
  offset: 0,
};

const initialState: StockState = {
  stockData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  totalValoAchat: 0,
  totalValoVente: 0,
  totalValoVenteTtc: 0,
  totalQuantite: 0,
  mouvementData: [],
  totalMouvement: 0,
  totalPagesMouvement: 0,
  currentPageMouvement: 0,
  totalEntreesQte: 0,
  totalSortiesQte: 0,
  totalEntreesValeur: 0,
  totalSortiesValeur: 0,
  loading: false,
  error: null,
  filters: initialFilters,
};

export const fetchStockData = createAsyncThunk<
  { data: StockArticle[]; total: number; totalPages: number; currentPage: number; totalValoAchat?: number; totalValoVente?: number; totalValoVenteTtc?: number; totalQuantite?: number },
  StockFilters,
  { rejectValue: string }
>(
  'stock/fetchStockData',
  async (params, thunkAPI) => {
    try {
      // sp_select_stock n'accepte pas date_debut/date_fin — on les retire
      const { date_debut, date_fin, filter_dept_id, ...rest } = params;
      // Si filter_dept_id null ("Tous") → on l'omet pour que le backend reçoive undefined
      // → SP p_departement_id = NULL → tous les depts.
      // Si number → on l'envoie tel quel.
      const spParams = {
        ...rest,
        ...(filter_dept_id !== null && { filter_dept_id }),
      };
      // signal : la page annule la requête précédente quand une autre page est demandée
      const response = await api.get('/stock/getByFilters', { params: spParams, signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement stock.');
    }
  }
);

export const fetchStockMouvements = createAsyncThunk<
  {
    data: StockMouvement[]; total: number; totalPages: number; currentPage: number;
    totalEntreesQte?: number; totalSortiesQte?: number;
    totalEntreesValeur?: number; totalSortiesValeur?: number;
  },
  StockFilters,
  { rejectValue: string }
>(
  'stock/fetchStockMouvements',
  async (params, thunkAPI) => {
    try {
      // sp_select_stock_mouvement n'a pas de filtre stock_sous_securite / masquer_stock_zero
      const { stock_sous_securite, masquer_stock_zero, filter_dept_id, ...rest } = params;
      const spParams = {
        ...rest,
        ...(filter_dept_id !== null && { filter_dept_id }),
      };
      const response = await api.get('/stock/mouvements/getByFilters', { params: spParams, signal: thunkAPI.signal });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement mouvements.');
    }
  }
);

const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<StockFilters>>) => {
      // Aucune valeur modifiée → état inchangé (même référence), donc pas de re-fetch.
      const keys = Object.keys(action.payload) as (keyof StockFilters)[];
      if (keys.every((k) => state.filters[k] === action.payload[k])) return;
      const isPageChange = 'offset' in action.payload;
      state.filters = {
        ...state.filters,
        ...action.payload,
        ...(!isPageChange && { offset: 0 }),
      };
    },
    resetFilters: (state) => {
      state.filters = initialFilters;
    },
    clearStockData: (state) => {
      state.stockData = [];
    },
    clearMouvementData: (state) => {
      state.mouvementData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockData.fulfilled, (state, action) => {
        state.loading        = false;
        state.stockData      = action.payload.data;
        state.total          = action.payload.total;
        state.totalPages     = action.payload.totalPages;
        state.currentPage    = action.payload.currentPage;
        state.totalValoAchat = action.payload.totalValoAchat ?? 0;
        state.totalQuantite = action.payload.totalQuantite ?? 0;
        state.totalValoVente = action.payload.totalValoVente ?? 0;
        state.totalValoVenteTtc = action.payload.totalValoVenteTtc ?? 0;
      })
      .addCase(fetchStockData.rejected, (state, action) => {
        // Requête annulée (remplacée par une plus récente) : ni erreur, ni fin de chargement
        if (action.meta.aborted) return;
        state.loading = false;
        state.error   = action.payload as string;
      })
      .addCase(fetchStockMouvements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockMouvements.fulfilled, (state, action) => {
        state.loading              = false;
        state.mouvementData        = action.payload.data;
        state.totalMouvement       = action.payload.total;
        state.totalPagesMouvement  = action.payload.totalPages;
        state.currentPageMouvement = action.payload.currentPage;
        state.totalEntreesQte      = action.payload.totalEntreesQte    ?? 0;
        state.totalSortiesQte      = action.payload.totalSortiesQte    ?? 0;
        state.totalEntreesValeur   = action.payload.totalEntreesValeur ?? 0;
        state.totalSortiesValeur   = action.payload.totalSortiesValeur ?? 0;
      })
      .addCase(fetchStockMouvements.rejected, (state, action) => {
        if (action.meta.aborted) return;   // cf. fetchStockData.rejected
        state.loading = false;
        state.error   = action.payload as string;
      });
  },
});

export const { setFilters, resetFilters, clearStockData, clearMouvementData } = stockSlice.actions;
export default stockSlice.reducer;
